// Page builder blocks API client (MOD-14 / Blok F).
//
// GET  /api/v1/pages/{id}/blocks → { useBuilder, blocks:[{blockType, displayOrder,
//      propsJson}], warnings:[] }. `propsJson` is a JSON *string* → parsed here.
// PUT  /api/v1/pages/{id}/blocks  body { useBuilder, blocks:[{blockType, props}] }.
//      Replace-all, order = array order, ≤50 blocks. BE validates each block's
//      props against its schema (400 PB_INVALID_BLOCK) and referential integrity
//      for formEmbed/sliderEmbed (400 PB_REF_NOT_FOUND). The BE is the
//      source-of-truth; we only serialize `props` objects (not `propsJson`).

import { http, unwrap, type ActionResponse } from './http';
import type { components } from '@/types/api';
import type { PageBlock } from '@features/pages/blocks/blockTypes';
import { isBlockType } from '@features/pages/blocks/blockTypes';

type PageBlocksDto = components['schemas']['PageBlocksDto'];
type PageBlockDto = components['schemas']['PageBlockDto'];

/** Hard ceiling mirrored from the BE (PUT rejects >50 blocks). */
export const MAX_PAGE_BLOCKS = 50;

export interface PageBlocksResult {
  useBuilder: boolean;
  blocks: PageBlock[];
  warnings: string[];
}

function parseProps(propsJson: string | null | undefined): Record<string, unknown> {
  if (!propsJson) return {};
  try {
    const parsed: unknown = JSON.parse(propsJson);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    // A malformed props payload should degrade to an empty block rather than
    // crash the editor; the BE still holds the raw value.
    return {};
  }
}

function mapBlock(d: PageBlockDto): PageBlock | null {
  const type = d.blockType ?? '';
  if (!isBlockType(type)) return null;
  return { blockType: type, props: parseProps(d.propsJson) };
}

function mapResult(d: PageBlocksDto): PageBlocksResult {
  const raw = d.blocks ?? [];
  const ordered = [...raw].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  return {
    useBuilder: d.useBuilder ?? false,
    blocks: ordered.map(mapBlock).filter((b): b is PageBlock => b !== null),
    warnings: (d.warnings ?? []).filter((w): w is string => typeof w === 'string'),
  };
}

const base = (pageId: string) => `/api/v1/pages/${pageId}/blocks`;

export const pageBlocksApi = {
  async get(pageId: string): Promise<PageBlocksResult> {
    const res = await http.get<ActionResponse<PageBlocksDto>>(base(pageId));
    return mapResult(unwrap(res));
  },

  async update(
    pageId: string,
    input: { useBuilder: boolean; blocks: PageBlock[] },
  ): Promise<void> {
    const body: components['schemas']['UpdatePageBlocksRequest'] = {
      useBuilder: input.useBuilder,
      blocks: input.blocks.map((b) => ({ blockType: b.blockType, props: b.props })),
    };
    const res = await http.put<ActionResponse<PageBlocksDto>>(base(pageId), body);
    unwrap(res);
  },
};
